import { useEffect, useState } from 'react';
import { api } from '../api';
import { Logo } from '../components';
import { useRouter } from '../router';
import { useApp } from '../store';

export function DiscordIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.32 4.37a19.8 19.8 0 0 0-4.89-1.52.07.07 0 0 0-.08.04c-.21.38-.44.87-.6 1.25a18.3 18.3 0 0 0-5.5 0 12.6 12.6 0 0 0-.61-1.25.07.07 0 0 0-.08-.04 19.7 19.7 0 0 0-4.88 1.52.06.06 0 0 0-.03.02C.53 9.05-.32 13.58.1 18.06a.08.08 0 0 0 .03.05 19.9 19.9 0 0 0 6 3.03.08.08 0 0 0 .08-.03c.46-.63.87-1.3 1.22-2a.08.08 0 0 0-.04-.1 13 13 0 0 1-1.87-.9.08.08 0 0 1 0-.12l.36-.29a.07.07 0 0 1 .08 0 14.2 14.2 0 0 0 12.06 0 .07.07 0 0 1 .08 0l.37.28a.08.08 0 0 1 0 .13c-.6.35-1.22.64-1.87.89a.08.08 0 0 0-.04.11c.36.7.77 1.36 1.22 1.99a.08.08 0 0 0 .08.03 19.8 19.8 0 0 0 6.02-3.03.08.08 0 0 0 .03-.05c.5-5.18-.84-9.68-3.55-13.66a.06.06 0 0 0-.03-.03zM8.02 15.33c-1.18 0-2.16-1.08-2.16-2.42 0-1.33.96-2.42 2.16-2.42 1.21 0 2.18 1.1 2.16 2.42 0 1.34-.96 2.42-2.16 2.42zm7.97 0c-1.18 0-2.15-1.08-2.15-2.42 0-1.33.95-2.42 2.15-2.42 1.22 0 2.18 1.1 2.16 2.42 0 1.34-.94 2.42-2.16 2.42z" />
    </svg>
  );
}

export function GoogleIcon({ size = 17 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M23.5 12.27c0-.85-.08-1.66-.22-2.45H12v4.63h6.45a5.52 5.52 0 0 1-2.4 3.62v3h3.87c2.27-2.09 3.58-5.17 3.58-8.8z" />
      <path fill="#34A853" d="M12 24c3.24 0 5.96-1.07 7.94-2.92l-3.87-3a7.24 7.24 0 0 1-10.8-3.8H1.28v3.1A12 12 0 0 0 12 24z" />
      <path fill="#FBBC05" d="M5.27 14.28a7.2 7.2 0 0 1 0-4.56v-3.1H1.28a12 12 0 0 0 0 10.76l3.99-3.1z" />
      <path fill="#EA4335" d="M12 4.77c1.77 0 3.35.61 4.6 1.8l3.43-3.43A11.98 11.98 0 0 0 1.28 6.62l3.99 3.1A7.24 7.24 0 0 1 12 4.77z" />
    </svg>
  );
}

const OAUTH_MESSAGES: Record<string, { message: string; tone: 'error' | 'success' }> = {
  'state-mismatch': { message: 'Sign-in was interrupted. Try again.', tone: 'error' },
  'linked-elsewhere': { message: 'That account is already linked to a different TeamCraft user.', tone: 'error' },
  'already-linked': { message: 'You already have an account linked for that provider.', tone: 'error' },
  'email-in-use': { message: 'That email already has a TeamCraft account. Sign in, then link the provider from your profile.', tone: 'error' },
  error: { message: 'Sign-in failed. Try again.', tone: 'error' }
};

export function useOAuthResultToast() {
  const { toast } = useApp();
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get('oauth');
    if (!result) return;
    const known = OAUTH_MESSAGES[result];
    if (known) toast(known.message, known.tone);
    else if (result.startsWith('linked-')) toast(`${result.slice(7).replace(/^./, (c) => c.toUpperCase())} account linked.`, 'success');
    else if (result.startsWith('welcome-')) toast('Welcome to TeamCraft! Set up your profile so squads know how you play.', 'success');
    else if (result === 'signed-in') toast('Signed in.', 'success');
    params.delete('oauth');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}`);
  }, [toast]);
}

function OAuthButtons() {
  const { providers } = useApp();
  if (!providers.discord && !providers.google) return null;
  return (
    <>
      <div className="oauth-buttons">
        {providers.discord && (
          <a className="oauth-button discord" href="/api/auth/oauth/discord/start">
            <DiscordIcon /> Continue with Discord
          </a>
        )}
        {providers.google && (
          <a className="oauth-button" href="/api/auth/oauth/google/start">
            <GoogleIcon /> Continue with Google
          </a>
        )}
      </div>
      <div className="auth-divider"><span>or use email</span></div>
    </>
  );
}

type Mode = 'signup' | 'login' | 'forgot';

export function AuthPage({ initialMode = 'signup' }: { initialMode?: 'signup' | 'login' }) {
  const { refreshAuth, toast } = useApp();
  const { navigate } = useRouter();
  const [mode, setMode] = useState<Mode>(initialMode);
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [notice, setNotice] = useState('');
  useOAuthResultToast();

  function switchMode(next: Mode) {
    setMode(next);
    setErrorMessage('');
    setNotice('');
  }

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setErrorMessage('');
    setNotice('');
    try {
      if (mode === 'signup') {
        await api.signup(email, username, password);
        await refreshAuth();
        toast('Welcome to TeamCraft! Set up your profile so squads know how you play.', 'success');
        navigate('/profile');
      } else if (mode === 'login') {
        await api.login(email, password);
        await refreshAuth();
        toast('Signed in.', 'success');
        navigate('/');
      } else {
        const result = await api.forgotPassword(email);
        if (result.devLink) {
          setNotice('Email sending is not configured yet — using the local dev link.');
          window.location.href = result.devLink;
          return;
        }
        setNotice(result.emailConfigured
          ? result.message
          : 'Email sending is not set up yet, so no reset email could go out. Contact the TeamCraft team for help.');
      }
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const headings: Record<Mode, [string, string]> = {
    signup: ['Create your account', 'Find teammates worth playing with again.'],
    login: ['Welcome back', 'Your squads are waiting.'],
    forgot: ['Reset your password', 'Enter your account email and we will send a reset link.']
  };

  return (
    <main className="auth-page">
      <div className="auth-card">
        <Logo />
        <h1>{headings[mode][0]}</h1>
        <p>{headings[mode][1]}</p>
        {mode !== 'forgot' && <OAuthButtons />}
        <form onSubmit={submit}>
          <label className="field">
            <span className="field-label">Email</span>
            <input value={email} onChange={(event) => setEmail(event.target.value)} type="email" autoComplete="email" required />
          </label>
          {mode === 'signup' && (
            <label className="field">
              <span className="field-label">Username</span>
              <input value={username} onChange={(event) => setUsername(event.target.value)} minLength={3} maxLength={24} autoComplete="username" required />
              <span className="field-hint">Letters, numbers, dashes, and underscores.</span>
            </label>
          )}
          {mode !== 'forgot' && (
            <label className="field">
              <span className="field-label">Password</span>
              <input value={password} onChange={(event) => setPassword(event.target.value)} type="password" minLength={8} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} required />
            </label>
          )}
          {errorMessage && <div className="form-error" role="alert">{errorMessage}</div>}
          {notice && <div className="form-notice" role="status">{notice}</div>}
          <button className="primary full" type="submit" disabled={busy}>
            {busy ? 'One moment…' : mode === 'signup' ? 'Create account' : mode === 'login' ? 'Sign in' : 'Send reset link'}
          </button>
        </form>
        {mode === 'login' && (
          <button className="ghost full" type="button" onClick={() => switchMode('forgot')}>
            Forgot your password?
          </button>
        )}
        <button className="ghost full" type="button" onClick={() => switchMode(mode === 'signup' ? 'login' : 'signup')}>
          {mode === 'signup' ? 'Already have an account? Sign in' : 'New here? Create an account'}
        </button>
      </div>
    </main>
  );
}
